import { Injectable, inject, signal } from '@angular/core';
import { TOAST_CONFIG } from './toast.config';
import type { ToastItem, ToastOptions, ToastVariant } from './toast.types';

@Injectable({ providedIn: 'root' })
export class ToastService {
  private readonly config = inject(TOAST_CONFIG);
  private readonly _toasts = signal<ToastItem[]>([]);
  private readonly timers = new Map<string, ReturnType<typeof setTimeout>>();
  private seq = 0;

  readonly toasts = this._toasts.asReadonly();

  show(options: ToastOptions): string {
    const variant: ToastVariant = options.variant ?? 'info';
    const durationMs =
      options.durationMs ??
      this.config.durationByVariant?.[variant] ??
      this.config.durationMs ??
      4000;
    const id = options.id ?? `toast-${++this.seq}`;
    const item: ToastItem = {
      id,
      title: options.title,
      message: options.message,
      variant,
      durationMs,
      dismissible: options.dismissible ?? true,
      createdAt: Date.now(),
    };

    this.clearTimer(id);
    this._toasts.update((list) => {
      const without = list.filter((toast) => toast.id !== id);
      const next = [...without, item];
      const max = this.config.maxVisible ?? 5;
      if (next.length <= max) {
        return next;
      }
      const overflow = next.slice(0, next.length - max);
      for (const old of overflow) {
        this.clearTimer(old.id);
      }
      return next.slice(-max);
    });

    if (durationMs > 0) {
      this.timers.set(
        id,
        setTimeout(() => this.dismiss(id), durationMs),
      );
    }

    return id;
  }

  success(message: string, options?: Omit<ToastOptions, 'message' | 'variant'>): string {
    return this.show({ ...options, message, variant: 'success' });
  }

  error(message: string, options?: Omit<ToastOptions, 'message' | 'variant'>): string {
    return this.show({ ...options, message, variant: 'error' });
  }

  info(message: string, options?: Omit<ToastOptions, 'message' | 'variant'>): string {
    return this.show({ ...options, message, variant: 'info' });
  }

  warning(message: string, options?: Omit<ToastOptions, 'message' | 'variant'>): string {
    return this.show({ ...options, message, variant: 'warning' });
  }

  dismiss(id: string): void {
    this.clearTimer(id);
    this._toasts.update((list) => list.filter((toast) => toast.id !== id));
  }

  clear(): void {
    for (const id of this.timers.keys()) {
      this.clearTimer(id);
    }
    this._toasts.set([]);
  }

  getZIndex(): number {
    return this.config.zIndex ?? 10050;
  }

  private clearTimer(id: string): void {
    const timer = this.timers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.timers.delete(id);
    }
  }
}
